import { RecordScreenFormCard } from "@/lib/Components/Screens/RecordScreen/Cards/RecordScreenFormCard";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { updateRelocationMutation } from "@/app/Relocations/GraphQL/relocationMutations";
import { useQueryClient } from "@tanstack/react-query";
import { useRelocationRecord } from "@/app/Relocations/Hooks/useRelocationRecord";
import { Currency, HireUnitType, Measurement } from "@/gql/graphql";
import { CurrencyTypeInput } from "@/app/Common/Components/CurrencyTypeInput";
import { MeasurementInput } from "@/app/Common/Components/MeasurementInput";
import { HireUnitTypeInput } from "@/app/Common/Components/HireUnitType";
import { DateInput } from "@/lib/Components/Form/Inputs/DateInput";
import { SwitchInput } from "@/lib/Components/Form/Inputs/SwitchInput";
import { useTranslations } from "use-intl";
import { useFormikContext } from "formik";
import { Cog6ToothIcon } from "@heroicons/react/20/solid";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { relocationConfigurationValidation } from "@/app/Relocations/Validation/relocationValidation";

type ConfigurationFormValues = {
  currency: Currency;
  measurement: Measurement;
  hire_unit_type: HireUnitType;
  fixed_latest_departure_date: string | null;
  is_ferry_required: boolean;
  available_from_date: string;
  available_to_date: string;
};

function ConfigurationFormFields() {
  const tForm = useTranslations("relocation.form");
  const { values } = useFormikContext<ConfigurationFormValues>();

  return (
    <div className="col-span-full grid grid-cols-3 gap-4 sm:grid-cols-6">
      <CurrencyTypeInput
        name="currency"
        label={tForm("currency")}
        className="col-span-3"
      />
      <MeasurementInput
        name="measurement"
        label={tForm("measurement")}
        className="col-span-3"
      />
      <HireUnitTypeInput
        name="hire_unit_type"
        label={tForm("hireUnit")}
        className="col-span-3"
      />
      <DateInput
        name="fixed_latest_departure_date"
        label={tForm("latestDeparture")}
        optionalLabel
        min={values.available_from_date}
        max={values.available_to_date}
        className="col-span-3"
        clearable
      />
      <SwitchInput
        name="is_ferry_required"
        label={tForm("requireFerryDate")}
        tooltip={tForm("requireFerryDateTooltip")}
        className="col-span-3"
      />
    </div>
  );
}

function ConfigurationViewState({
  record,
}: {
  record: ConfigurationFormValues;
}) {
  const tForm = useTranslations("relocation.form");
  const t = useTranslations("relocation");

  return (
    <dl className="col-span-full grid grid-cols-2 gap-4 text-sm">
      <div>
        <dt className="text-gray-500">{tForm("currency")}</dt>
        <dd className="font-medium">{record.currency}</dd>
      </div>
      <div>
        <dt className="text-gray-500">{tForm("measurement")}</dt>
        <dd className="font-medium">{record.measurement}</dd>
      </div>
      <div>
        <dt className="text-gray-500">{tForm("hireUnit")}</dt>
        <dd className="font-medium">
          {t(`hire_unit_type_capitalized.${record.hire_unit_type}`)}
        </dd>
      </div>
      <div>
        <dt className="text-gray-500">{tForm("latestDeparture")}</dt>
        <dd className="font-medium">
          {record.fixed_latest_departure_date ?? "--"}
        </dd>
      </div>
      <div>
        <dt className="text-gray-500">{tForm("requireFerryDate")}</dt>
        <dd className="font-medium">
          {record.is_ferry_required ? "Yes" : "No"}
        </dd>
      </div>
    </dl>
  );
}

export function ConfigurationFormCard() {
  const queryClient = useQueryClient();
  const { mutateAsync } = useGqlMutation(updateRelocationMutation, {
    onSuccess: () => queryClient.invalidateQueries(),
  });
  const record = useRelocationRecord();
  const tForm = useTranslations("relocation.form");

  const initialValues: ConfigurationFormValues = {
    currency: record.currency,
    measurement: record.measurement,
    hire_unit_type: record.hire_unit_type,
    fixed_latest_departure_date: record.fixed_latest_departure_date ?? null,
    is_ferry_required: record.is_ferry_required ?? false,
    available_from_date: record.available_from_date,
    available_to_date: record.available_to_date,
  };

  return (
    <RecordScreenFormCard<ConfigurationFormValues>
      containerClassName="rounded-lg border border-gray-200 bg-white p-6"
      title={
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-orange-100 p-2">
            <Cog6ToothIcon className="h-5 w-5 text-orange-600" />
          </div>
          <div>
            <div>{tForm("configurationTitle")}</div>
            <p className="text-sm font-normal text-gray-500">
              {tForm("configurationDescription")}
            </p>
          </div>
        </div>
      }
      initialValues={initialValues}
      validationSchema={toFormikValidationSchema(
        relocationConfigurationValidation,
      )}
      view={<ConfigurationViewState record={initialValues} />}
      onSubmit={async (values) => {
        await mutateAsync({
          id: record.id,
          input: {
            currency: values.currency,
            measurement: values.measurement,
            hire_unit_type: values.hire_unit_type,
            fixed_latest_departure_date: values.fixed_latest_departure_date,
            is_ferry_required: values.is_ferry_required,
          },
        });
      }}
    >
      <ConfigurationFormFields />
    </RecordScreenFormCard>
  );
}
